import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Eye, EyeOff, UserPlus, X, Contact } from 'lucide-react';
import { apiClient } from '../../../lib/api';

interface AdminRegistrationFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AdminRegistrationForm: React.FC<AdminRegistrationFormProps> = ({ open, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        names: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // Estados para errores y UI
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Limpiar el formulario cuando se abre/cierra
    useEffect(() => {
        if (open) {
            setFormData({ names: '', lastName: '', email: '', password: '', confirmPassword: '' });
            setErrors({});
            setGlobalError(null);
            setIsSubmitting(false);
        }
    }, [open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Validación
    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.names.trim()) newErrors.names = 'El nombre es requerido';
        if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es requerido';

        if (!formData.email.trim()) {
            newErrors.email = 'El correo es requerido';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Introduce un correo electrónico válido';
        }

        if (formData.password.length < 8) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setGlobalError(null);

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            await apiClient.post('CreateSupportUser', {
                name: formData.names,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            // Manejo del error en caso de que el correo ya exista u otro error de validación del backend
            setGlobalError(
                error?.response?.data?.resultError?.message ||
                "Ocurrió un error al registrar el usuario de soporte."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            {/* Contenedor del Modal */}
            <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-[420px] max-h-[90vh] flex flex-col shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">

                {/* Botón Cerrar */}
                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors z-10 disabled:opacity-50"
                >
                    <X size={20} />
                </button>

                <div className="p-7 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">

                    {/* Header */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="w-[60px] h-[60px] rounded-full bg-gray-100 flex items-center justify-center mb-4">
                            <UserPlus size={30} className="text-gray-500" />
                        </div>
                        <h2 className="text-[22px] font-bold text-gray-900">Nuevo Administrador</h2>
                        <p className="text-[13px] text-gray-500 text-center mt-1">
                            Crea un usuario de soporte para el portal Admin.
                        </p>
                    </div>

                    {globalError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg text-center font-semibold">
                            {globalError}
                        </div>
                    )}

                    {/* Formulario */}
                    <form onSubmit={onSubmit} className="flex flex-col gap-3.5">

                        {/* Nombres */}
                        <div>
                            <div className="relative flex items-center">
                                <User size={18} className="absolute left-3 text-gray-400" />
                                <input
                                    type="text"
                                    name="names"
                                    value={formData.names}
                                    onChange={handleChange}
                                    placeholder="Nombres"
                                    className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-lg text-[13px] outline-none transition-all placeholder:text-gray-400
                                    ${errors.names ? "border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"}`}
                                />
                            </div>
                            {errors.names && <span className="text-[10px] text-red-500 mt-1 ml-1">{errors.names}</span>}
                        </div>

                        {/* Apellidos */}
                        <div>
                            <div className="relative flex items-center">
                                <Contact size={18} className="absolute left-3 text-gray-400" />
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Apellidos"
                                    className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-lg text-[13px] outline-none transition-all placeholder:text-gray-400
                                    ${errors.lastName ? "border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"}`}
                                />
                            </div>
                            {errors.lastName && <span className="text-[10px] text-red-500 mt-1 ml-1">{errors.lastName}</span>}
                        </div>

                        {/* Email */}
                        <div>
                            <div className="relative flex items-center">
                                <Mail size={18} className="absolute left-3 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Correo electrónico"
                                    className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-lg text-[13px] outline-none transition-all placeholder:text-gray-400
                                    ${errors.email ? "border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"}`}
                                />
                            </div>
                            {errors.email && <span className="text-[10px] text-red-500 mt-1 ml-1">{errors.email}</span>}
                        </div>

                        {/* Contraseña */}
                        <div>
                            <div className="relative flex items-center">
                                <Lock size={18} className="absolute left-3 text-gray-400" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Contraseña"
                                    className={`w-full pl-10 pr-10 py-2.5 bg-white border rounded-lg text-[13px] outline-none transition-all placeholder:text-gray-400
                                    ${errors.password ? "border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {errors.password && <span className="text-[10px] text-red-500 mt-1 ml-1">{errors.password}</span>}
                        </div>

                        {/* Confirmar Contraseña */}
                        <div>
                            <div className="relative flex items-center">
                                <Lock size={18} className="absolute left-3 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirmar Contraseña"
                                    className={`w-full pl-10 pr-10 py-2.5 bg-white border rounded-lg text-[13px] outline-none transition-all placeholder:text-gray-400
                                    ${errors.confirmPassword ? "border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {errors.confirmPassword && <span className="text-[10px] text-red-500 mt-1 ml-1">{errors.confirmPassword}</span>}
                        </div>

                        {/* Botón de Crear Admin */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full mt-2 py-2.5 rounded-lg text-[13px] font-semibold bg-[#4f6df5] text-white hover:bg-[#3b5bdb] transition-all flex items-center justify-center shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <><svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" strokeDasharray="30" strokeLinecap="round" />
                                </svg> Procesando...</>
                            ) : (
                                "Crear Usuario de Soporte"
                            )}
                        </button>
                    </form>

                </div>
            </div>
        </div>
    );
};

export default AdminRegistrationForm;